/**
 * Clamps a value between a minimum and maximum value.
 *
 * @param value - The value to clamp.
 * @param min - The minimum value.
 * @param max - The maximum value.
 * @returns The clamped value.
 */
function clamp(value: number, min: number, max: number) {
	return Math.max(min, Math.min(max, value));
}

/**
 * Maps a value from one range to another.
 *
 * @param x - The value to map.
 * @param a1 - The start of the first range.
 * @param a2 - The end of the first range.
 * @param b1 - The start of the second range.
 * @param b2 - The end of the second range.
 * @returns The mapped value.
 */
function mapLinear(x: number, a1: number, a2: number, b1: number, b2: number) {
	return b1 + ((x - a1) * (b2 - b1)) / (a2 - a1);
}

// Linear mapping from range <a1, a2> to range <b1, b2> with clamping at b1 and b2
function clampedMapLinear(x: number, a1: number, a2: number, b1: number, b2: number) {
	return clamp(mapLinear(x, a1, a2, b1, b2), Math.min(b1, b2), Math.max(b1, b2));
}

/**
 * Linear interpolation.
 * @param x - The value to interpolate.
 * @param y - The value to interpolate.
 * @param t - The interpolation value.
 * @returns The interpolated value.
 */
function lerp(x: number, y: number, t: number) {
	return (1 - t) * x + t * y;
}

/**
 * Rounds a number to a specified number of decimal places.
 *
 * @param value - The number to round.
 * @param d - The number of decimal places to round to.
 * @returns The rounded number.
 */
function decimals(value: number, d: number) {
	const p = Math.pow(10, Math.max(0, d));
	return Math.round(value * p) / p;
}

const math = {
	clamp,
	mapLinear,
	lerp,
	decimals,
	clampedMapLinear,
};

export { clamp, mapLinear, clampedMapLinear, lerp, decimals };

export default math;
